// ServicesView.jsx — View PURA da /services (View/ViewModel à risca).
// Recebe { vm, actions } — sem fetch, sem photoService, sem formatação, sem lógica de negócio.
// Contrato: services.vm.js (@version 1). Estados cobertos: loading, error, empty,
// ready, e ready+items:[] com filters.isFiltered (emptyFilteredMessage).
//
// Neutros vêm de --bg-page/--divider/--text-* (papéis themeáveis) e NÃO da escala --slate-*,
// que é global fixa e clara — usá-la como cor de texto quebra temas de superfície escura.
// Regras seguidas: estilo só via `sx`; cores/medidas só via var(--*); MUI v6 +
// @mui/icons-material; tipografia via <Typography variant>; useState apenas de UI
// (ids de foto que falharam no carregamento → cai no avatar de initials).
import { useState } from 'react';
import {
  Box, Typography, Button, TextField, InputAdornment, Select, MenuItem,
  IconButton, CircularProgress, Tooltip,
} from '@mui/material';
import DesignServices from '@mui/icons-material/DesignServices';
import Add from '@mui/icons-material/Add';
import Search from '@mui/icons-material/Search';
import FilterAltOff from '@mui/icons-material/FilterAltOff';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import PhotoCameraRounded from '@mui/icons-material/PhotoCameraRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import PaidRounded from '@mui/icons-material/PaidRounded';
import LocalOfferRounded from '@mui/icons-material/LocalOfferRounded';
import SearchOffRounded from '@mui/icons-material/SearchOffRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import ReplayRounded from '@mui/icons-material/ReplayRounded';

const CARD_SX = {
  bgcolor: 'var(--surface)',
  border: '1px solid',
  borderColor: 'var(--divider)',
  borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-card)' },
  boxShadow: 'var(--shadow-card)',
};

// 1 → 2 → 3 → 4 colunas; 5 a partir de 1700px (fora dos breakpoints do MUI).
const GRID_SX = {
  display: 'grid',
  gap: 'var(--space-2)',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(3, minmax(0, 1fr))',
    xl: 'repeat(4, minmax(0, 1fr))',
  },
  '@media (min-width:1700px)': { gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' },
};

/* ── Chip de atributo (duração / preço / categoria) ───────────────────── */
function AttrChip({ icon, label, tone = 'neutral' }) {
  const t = {
    neutral: { bg: 'var(--bg-page)', border: 'var(--divider)', fg: 'var(--text-secondary)' },
    price: { bg: 'var(--tint-success-bg)', border: 'var(--tint-success-border)', fg: 'var(--tint-success-fg)' },
    category: { bg: 'var(--color-primary-soft)', border: 'var(--color-primary-soft)', fg: 'var(--color-primary-dark)' },
  }[tone];
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.625,
        bgcolor: t.bg, border: '1px solid', borderColor: t.border,
        borderRadius: 999, px: 1.125, py: '3px', maxWidth: '100%',
      }}
    >
      <Box component="span" sx={{ display: 'flex', color: t.fg, fontSize: 'var(--fs-caption)' }}>{icon}</Box>
      <Typography variant="caption" sx={{ color: t.fg, fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </Typography>
    </Box>
  );
}

/* ── Foto do serviço: <img> e, no onError, avatar de initials ─────────── */
function ServicePhoto({ item, broken, onBroken, onClick }) {
  const showImg = !!item.photoUrl && !broken;
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`Trocar foto de ${item.name}`}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      sx={{
        position: 'relative', flexShrink: 0, cursor: 'pointer',
        width: 64, height: 64, borderRadius: 'var(--radius-sm)', overflow: 'hidden',
        bgcolor: 'var(--color-primary-soft)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        outline: 'none',
        '&:focus-visible': { boxShadow: '0 0 0 3px var(--color-primary-light)' },
        '&:hover .servicePhotoOverlay, &:focus-visible .servicePhotoOverlay': { opacity: 1 },
      }}
    >
      {showImg ? (
        <Box
          component="img"
          src={item.photoUrl}
          alt={item.name}
          onError={onBroken}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <Typography variant="subtitle1" sx={{ color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)' }}>
          {item.initials}
        </Typography>
      )}
      <Box
        className="servicePhotoOverlay"
        sx={{
          position: 'absolute', inset: 0, opacity: 0, transition: 'opacity .15s',
          bgcolor: 'rgba(27,33,48,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <PhotoCameraRounded sx={{ fontSize: 22, color: 'var(--on-primary)' }} />
      </Box>
    </Box>
  );
}

/* ── Card de serviço ──────────────────────────────────────────────────── */
function ServiceCard({ item, broken, onBroken, a }) {
  const chips = [
    item.durationLabel && { key: 'd', icon: <ScheduleRounded sx={{ fontSize: 14 }} />, label: item.durationLabel, tone: 'neutral' },
    item.priceLabel && { key: 'p', icon: <PaidRounded sx={{ fontSize: 14 }} />, label: item.priceLabel, tone: 'price' },
    item.category && { key: 'c', icon: <LocalOfferRounded sx={{ fontSize: 14 }} />, label: item.category, tone: 'category' },
  ].filter(Boolean);

  return (
    <Box
      component="article"
      sx={{
        ...CARD_SX,
        p: { xs: 2, sm: 2.25 },
        display: 'flex', flexDirection: 'column', gap: 1.5,
        transition: 'box-shadow .15s, border-color .15s',
        '&:hover': { boxShadow: 'var(--shadow-hover)', borderColor: 'var(--color-primary-light)' },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'flex-start' }, gap: 1.75 }}>
        <ServicePhoto item={item} broken={broken} onBroken={onBroken} onClick={() => a.onPhoto?.(item.id)} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)', lineHeight: 1.3, overflowWrap: 'anywhere' }}
          >
            {item.name}
          </Typography>
          {item.displayName && (
            <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', mt: '2px' }}>
              Público: {item.displayName}
            </Typography>
          )}
          {item.description && (
            <Typography
              variant="body2"
              sx={{
                color: 'var(--text-secondary)', mt: 0.75, lineHeight: 1.45,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}
            >
              {item.description}
            </Typography>
          )}
        </Box>
      </Box>

      {chips.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {chips.map((c) => <AttrChip key={c.key} icon={c.icon} label={c.label} tone={c.tone} />)}
        </Box>
      )}

      <Box
        sx={{
          mt: 'auto', pt: 1.25, borderTop: '1px solid', borderColor: 'var(--divider)',
          display: 'flex', justifyContent: 'flex-end', gap: 0.5,
        }}
      >
        <Tooltip title="Trocar foto">
          <IconButton
            aria-label={`Trocar foto de ${item.name}`}
            onClick={() => a.onPhoto?.(item.id)}
            sx={{ width: 40, height: 40, color: 'var(--text-secondary)', '&:hover': { color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' } }}
          >
            <PhotoCameraRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Editar">
          <IconButton
            aria-label={`Editar ${item.name}`}
            onClick={() => a.onEdit?.(item.id)}
            sx={{ width: 40, height: 40, color: 'var(--text-secondary)', '&:hover': { color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' } }}
          >
            <EditRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Excluir">
          <IconButton
            aria-label={`Excluir ${item.name}`}
            onClick={() => a.onDelete?.(item.id)}
            sx={{ width: 40, height: 40, color: 'var(--text-secondary)', '&:hover': { color: 'var(--color-error)', bgcolor: 'var(--tint-error-bg)' } }}
          >
            <DeleteOutlineRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

/* ── Barra de filtros ─────────────────────────────────────────────────── */
function FiltersBar({ filters, a }) {
  const f = filters || {};
  return (
    <Box
      sx={{
        ...CARD_SX,
        p: { xs: 1.5, sm: 1.75 }, mb: 'var(--space-2)',
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25,
      }}
    >
      <TextField
        value={f.searchText || ''}
        onChange={(e) => a.onSearchChange?.(e.target.value)}
        placeholder="Buscar serviço por nome…"
        size="small"
        inputProps={{ 'aria-label': 'Buscar serviço' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ fontSize: 20, color: 'var(--text-secondary)' }} />
            </InputAdornment>
          ),
        }}
        sx={{ flex: '1 1 260px', minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: 'var(--radius-sm)' } }}
      />

      <Select
        value={f.sortOrder || ''}
        onChange={(e) => a.onSortChange?.(e.target.value)}
        size="small"
        inputProps={{ 'aria-label': 'Ordenar serviços' }}
        sx={{ flex: '0 1 200px', minWidth: 170, borderRadius: 'var(--radius-sm)' }}
      >
        {(f.sortOptions || []).map((o) => (
          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
        ))}
      </Select>

      <Button
        onClick={() => a.onClearFilters?.()}
        disabled={!f.canClear}
        startIcon={<FilterAltOff sx={{ fontSize: 18 }} />}
        sx={{
          minHeight: 40, px: 1.75, borderRadius: 'var(--radius-sm)',
          border: '1px solid', borderColor: 'var(--divider)',
          color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', textTransform: 'none',
          '&:hover': { borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
        }}
      >
        Limpar
      </Button>
    </Box>
  );
}

/* ── Blocos de estado ─────────────────────────────────────────────────── */
function StateBlock({ icon, title, description, action }) {
  return (
    <Box sx={{ ...CARD_SX, px: 3, py: { xs: 5, sm: 7 }, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      <Box
        sx={{
          width: 64, height: 64, borderRadius: '50%', mb: 2,
          bgcolor: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)', mb: 0.5 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', maxWidth: 420 }}>
          {description}
        </Typography>
      )}
      {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
    </Box>
  );
}

/* ── View ─────────────────────────────────────────────────────────────── */
export default function ServicesView({ vm, actions }) {
  const a = actions || {};
  const [brokenPhotos, setBrokenPhotos] = useState({}); // UI-only: ids cuja foto falhou
  const markBroken = (id) => setBrokenPhotos((prev) => (prev[id] ? prev : { ...prev, [id]: true }));

  const items = vm.items || [];
  const filtered = !!vm.filters?.isFiltered;
  const showFilters = vm.viewState === 'ready';

  const newButton = (
    <Button
      onClick={() => a.onNew?.()}
      startIcon={<Add />}
      disableElevation
      sx={{
        bgcolor: 'var(--color-primary)', color: 'var(--on-primary)',
        borderRadius: 'var(--radius-sm)', px: 2.25, minHeight: 42,
        fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
        '&:hover': { bgcolor: 'var(--color-primary-dark)' },
      }}
    >
      {vm.actionLabel}
    </Button>
  );

  let body = null;

  if (vm.viewState === 'loading') {
    body = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress size={32} aria-label="Carregando serviços" sx={{ color: 'var(--color-primary)' }} />
      </Box>
    );
  } else if (vm.viewState === 'error') {
    body = (
      <StateBlock
        icon={<ErrorRounded sx={{ fontSize: 32, color: 'var(--color-error)' }} />}
        title="Não foi possível carregar os serviços"
        description={vm.errorMessage}
        action={(
          <Button
            onClick={() => a.onRetry?.()}
            startIcon={<ReplayRounded />}
            sx={{
              minHeight: 42, px: 2.25, borderRadius: 'var(--radius-sm)',
              border: '1px solid', borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)',
              fontWeight: 'var(--fw-bold)', textTransform: 'none',
              '&:hover': { bgcolor: 'var(--color-primary-soft)' },
            }}
          >
            Tentar novamente
          </Button>
        )}
      />
    );
  } else if (vm.viewState === 'empty') {
    body = (
      <StateBlock
        icon={<DesignServices sx={{ fontSize: 30, color: 'var(--text-secondary)' }} />}
        title="Nenhum serviço cadastrado"
        description="Cadastre os serviços que você oferece para que apareçam na sua agenda e na página pública de agendamento."
        action={newButton}
      />
    );
  } else if (items.length === 0) {
    body = (
      <StateBlock
        icon={<SearchOffRounded sx={{ fontSize: 30, color: 'var(--text-secondary)' }} />}
        title={vm.emptyFilteredMessage}
        description={filtered ? 'Revise a busca ou limpe os filtros para ver todos os serviços.' : null}
        action={vm.filters?.canClear ? (
          <Button
            onClick={() => a.onClearFilters?.()}
            startIcon={<FilterAltOff sx={{ fontSize: 18 }} />}
            sx={{
              minHeight: 42, px: 2.25, borderRadius: 'var(--radius-sm)',
              border: '1px solid', borderColor: 'var(--divider)', color: 'var(--text-secondary)',
              fontWeight: 'var(--fw-medium)', textTransform: 'none',
              '&:hover': { borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
            }}
          >
            Limpar filtros
          </Button>
        ) : null}
      />
    );
  } else {
    body = (
      <Box sx={GRID_SX}>
        {items.map((item) => (
          <ServiceCard
            key={item.id}
            item={item}
            broken={!!brokenPhotos[item.id]}
            onBroken={() => markBroken(item.id)}
            a={a}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ color: 'var(--text-primary)' }}>
      <Box
        sx={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: 2, flexWrap: 'wrap', mb: 'var(--space-3)',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DesignServices sx={{ fontSize: 30, color: 'var(--color-primary-dark)' }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)' }}>
              {vm.title}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.5 }}>
            {vm.subtitle}
          </Typography>
        </Box>
        {vm.viewState !== 'empty' && newButton}
      </Box>

      {showFilters && <FiltersBar filters={vm.filters} a={a} />}
      {body}
    </Box>
  );
}
